/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InteractiveComponents } from '../../../core/enums';
import { Log } from '../../../core/logging/Log';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import {
    setHomeScreenFocusAction,
    setHomeScreenGhostAction,
    setHomeScreenNavFocusAction,
    setHomeScreenPreviewAction,
    setHomeScreenVerticalScrollAction,
} from '../homescreen.actions';
import {
    checkAndLoadMoreCarousels,
    checkAndLoadMoreFeedItems,
    fetchPreviewForFocusedTile,
} from '../homescreen.reducers';

const TAG = 'homeScreenMouseOperations';

const focusCarouselAndPreview = (
    dispatch: Function,
    carouselIndex: number,
    carousels: any[],
    tileIndexPerCarousel: Record<number, number>,
) => {
    const tileCount = carousels[carouselIndex]?.feedItems?.feedItems?.length ?? 0;
    const savedTileIndex = tileIndexPerCarousel[carouselIndex] ?? 0;
    const tileIndex = Math.min(savedTileIndex, Math.max(0, tileCount - 1));
    dispatch(setHomeScreenFocusAction({ carouselIndex, tileIndex }));
    dispatch(setHomeScreenPreviewAction(null));
    dispatch(fetchPreviewForFocusedTile());
};

const applyGhostScroll = (
    dispatch: Function,
    ghostState: any,
    carouselIndex: number,
    newOffset: number,
) => {
    dispatch(
        setHomeScreenGhostAction({ ...ghostState, scrollOffset: newOffset, tileIndex: newOffset }),
    );
    dispatch(setHomeScreenFocusAction({ carouselIndex, tileIndex: newOffset }));
    dispatch(setHomeScreenPreviewAction(null));
    dispatch(fetchPreviewForFocusedTile());
};

/**
 * Parse a data-value string.
 * Tile values: "{tileIndex}_{carouselIndex}" (e.g. "2_0")
 * Nav values: "nav_{navIndex}" (e.g. "nav_1")
 * Caret values: "NORTH", "SOUTH"
 */
const parseValue = (
    value: string | undefined,
):
    | { type: 'tile'; tileIndex: number; carouselIndex: number }
    | { type: 'nav'; navIndex: number }
    | null => {
    if (!value) return null;
    if (value.startsWith('nav_')) {
        const navIndex = Number(value.substring(4));
        if (Number.isNaN(navIndex)) return null;
        return { type: 'nav', navIndex };
    }
    if (value.includes('_')) {
        const parts = value.split('_');
        const tileIndex = Number(parts[0]);
        const carouselIndex = Number(parts[1]);
        if (Number.isNaN(tileIndex) || Number.isNaN(carouselIndex)) return null;
        return { type: 'tile', tileIndex, carouselIndex };
    }
    return null;
};

const isCaretDirection = (value: string | undefined): boolean => {
    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
        case InteractiveComponents.EAST:
        case InteractiveComponents.WEST:
            return true;
        default:
            return false;
    }
};

/**
 * Mouse hover on a HomeScreen element.
 * Reads inputFactory.value to determine what was hovered and
 * sets ghost highlight state (does NOT move actual focus — avoids auto-scroll).
 */
export const onMouseOverHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    // Caret hover → activate caret and restore ghost carousel context.
    // EAST/WEST carets only appear on the focused carousel, so we always
    // use focusState.carouselIndex — no separate cache needed.
    if (isCaretDirection(value)) {
        const { ghostState, focusState } = getState().homeScreen;
        // If ghost was cleared by a key press, treat as fresh mouse session.
        if (
            (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) &&
            ghostState.carouselIndex === null
        ) {
            return;
        }
        dispatch(setActiveInteractiveComponent(value));
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(
                setHomeScreenGhostAction({
                    carouselIndex: focusState.carouselIndex,
                    tileIndex: null,
                    navBarIndex: null,
                    scrollOffset: ghostState.scrollOffset,
                }),
            );
        }
        return;
    }

    const parsed = parseValue(value);
    if (!parsed) return;

    if (parsed.type === 'nav') {
        Log.d(TAG, `Ghost nav: ${parsed.navIndex}`);
        dispatch(
            setHomeScreenGhostAction({
                carouselIndex: null,
                tileIndex: null,
                navBarIndex: parsed.navIndex,
                scrollOffset: 0,
            }),
        );
    } else if (parsed.type === 'tile') {
        Log.d(TAG, `Ghost tile: ${parsed.tileIndex}_${parsed.carouselIndex}`);
        const { ghostState, focusState } = getState().homeScreen;
        // Preserve scrollOffset when hovering within the same carousel.
        // When hovering the focused carousel for the first time after a key
        // press cleared the ghost, sync the offset to the actual focus
        // position so that EAST/WEST carets reflect the real scroll state.
        let keepOffset: number;
        if (ghostState.carouselIndex === parsed.carouselIndex) {
            keepOffset = ghostState.scrollOffset;
        } else if (parsed.carouselIndex === focusState.carouselIndex) {
            keepOffset = focusState.tileIndex;
        } else {
            keepOffset = 0;
        }
        dispatch(
            setHomeScreenGhostAction({
                carouselIndex: parsed.carouselIndex,
                tileIndex: parsed.tileIndex,
                navBarIndex: null,
                scrollOffset: keepOffset,
            }),
        );
    }
};

/**
 * Mouse click on a HomeScreen element.
 * Moves actual focus to the ghost position, clears ghost.
 * Returns `true` when the caller should follow up with an OK action
 * (tile / nav click). Returns `false` for caret clicks (handled internally).
 * Caret clicks are routed to the interactive layer handler.
 */
export const onMouseClickHomeScreenOperations =
    () =>
    (dispatch: Function, getState: TGetState): boolean => {
        const { inputFactory, homeScreen } = getState();
        const { value } = inputFactory;

        // Caret click → delegate to interactive layer handler
        if (isCaretDirection(value)) {
            dispatch(onMouseClickHomeScreenInteractiveLayerOperations());
            return false;
        }

        const parsed = parseValue(value);
        if (!parsed) return false;

        // Move real focus to the ghost position
        if (parsed.type === 'nav') {
            dispatch(setHomeScreenNavFocusAction(true, parsed.navIndex));
            dispatch(setHomeScreenPreviewAction(null));
        } else if (parsed.type === 'tile') {
            dispatch(setHomeScreenNavFocusAction(false, homeScreen.navBarIndex));
            dispatch(setHomeScreenVerticalScrollAction(parsed.carouselIndex));
            dispatch(
                setHomeScreenFocusAction({
                    carouselIndex: parsed.carouselIndex,
                    tileIndex: parsed.tileIndex,
                }),
            );
            dispatch(setHomeScreenPreviewAction(null));
            dispatch(fetchPreviewForFocusedTile());
            dispatch(checkAndLoadMoreFeedItems());
            dispatch(checkAndLoadMoreCarousels());
        }

        // Clear ghost highlight
        const clearGhost = {
            carouselIndex: null,
            tileIndex: null,
            navBarIndex: null,
            scrollOffset: 0,
        };
        dispatch(setHomeScreenGhostAction(clearGhost));

        return true;
    };

/**
 * Mouse out on a HomeScreen element — clear ghost highlight and interactive layer.
 * When leaving a caret, preserve the ghost carousel context so the sister caret
 * stays visible (e.g. WEST caret stays after clicking EAST).
 */
export const onMouseOutHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory, homeScreen } = getState();
    const { value } = inputFactory;
    const { ghostState } = homeScreen;

    if (isCaretDirection(value)) {
        dispatch(setActiveInteractiveComponent(''));
        // Preserve carousel context for the sister caret — keep carouselIndex
        // and scrollOffset from Redux ghost state but clear the tile frame.
        dispatch(
            setHomeScreenGhostAction({
                carouselIndex: ghostState.carouselIndex,
                tileIndex: null,
                navBarIndex: null,
                scrollOffset: ghostState.scrollOffset,
            }),
        );
        return;
    }

    // Leaving a non-caret element — preserve ghost carousel context so
    // EAST/WEST carets stay visible during tile-to-caret transitions.
    // Only clear the tile frame (tileIndex/navBarIndex).
    dispatch(
        setHomeScreenGhostAction({
            carouselIndex: ghostState.carouselIndex,
            tileIndex: null,
            navBarIndex: null,
            scrollOffset: ghostState.scrollOffset,
        }),
    );
};

/** Number of tiles visible in the viewport at once (page size for EAST/WEST caret scroll). */
const TILES_PER_PAGE = 4;

/**
 * Mouse click on InteractiveLayer caret — scroll carousels (NORTH/SOUTH)
 * or scroll tile list within the ghost carousel (EAST/WEST).
 * Uses inputFactory.value (the clicked element's data-value) directly
 * rather than interactiveLayer.activeComponent, because mouseout may
 * have cleared activeComponent before the click fires.
 */
export const onMouseClickHomeScreenInteractiveLayerOperations =
    () => (dispatch: Function, getState: TGetState) => {
        const { inputFactory, homeScreen } = getState();
        const clickedDirection = inputFactory.value;
        const { focusState, carousels, ghostState, tileIndexPerCarousel } = homeScreen;

        Log.d(TAG, `Caret click: ${clickedDirection}`);

        switch (clickedDirection) {
            case InteractiveComponents.NORTH: {
                if (focusState.carouselIndex === 0 && !homeScreen.navBarFocused) {
                    // At first carousel — no north caret visible, ignore click
                    break;
                }
                if (focusState.carouselIndex > 0) {
                    const isLastPage = !homeScreen.pageCursor;
                    const isAtLastCarousel = focusState.carouselIndex === carousels.length - 1;
                    const highlightIsAhead =
                        isLastPage &&
                        isAtLastCarousel &&
                        homeScreen.verticalScrollIndex < focusState.carouselIndex;
                    if (highlightIsAhead) {
                        // Returning from last → second-to-last: list already positioned, just move focus
                        focusCarouselAndPreview(
                            dispatch,
                            focusState.carouselIndex - 1,
                            carousels,
                            tileIndexPerCarousel,
                        );
                    } else {
                        dispatch(
                            setHomeScreenVerticalScrollAction(
                                Math.max(0, homeScreen.verticalScrollIndex - 1),
                            ),
                        );
                        focusCarouselAndPreview(
                            dispatch,
                            focusState.carouselIndex - 1,
                            carousels,
                            tileIndexPerCarousel,
                        );
                    }
                }
                break;
            }
            case InteractiveComponents.SOUTH: {
                if (homeScreen.navBarFocused) {
                    // From nav bar, move focus to first carousel
                    dispatch(setHomeScreenNavFocusAction(false, homeScreen.navBarIndex));
                    dispatch(fetchPreviewForFocusedTile());
                    break;
                }
                if (focusState.carouselIndex < carousels.length - 1) {
                    const isLastPage = !homeScreen.pageCursor;
                    const isAtSecondToLast = focusState.carouselIndex === carousels.length - 2;
                    if (isLastPage && isAtSecondToLast) {
                        // Last page, going to last carousel — only move focus, keep list in place
                        focusCarouselAndPreview(
                            dispatch,
                            focusState.carouselIndex + 1,
                            carousels,
                            tileIndexPerCarousel,
                        );
                    } else {
                        dispatch(
                            setHomeScreenVerticalScrollAction(
                                Math.min(carousels.length - 1, homeScreen.verticalScrollIndex + 1),
                            ),
                        );
                        focusCarouselAndPreview(
                            dispatch,
                            focusState.carouselIndex + 1,
                            carousels,
                            tileIndexPerCarousel,
                        );
                        dispatch(checkAndLoadMoreCarousels());
                    }
                }
                break;
            }
            case InteractiveComponents.EAST: {
                if (ghostState.carouselIndex == null) break;
                const carousel = carousels[ghostState.carouselIndex];
                if (!carousel) break;
                const tileCount = carousel.feedItems?.feedItems?.length ?? 0;
                // Always scroll a full page; allow offset up to tileCount-1 so
                // even a single extra tile beyond the viewport triggers a full shift.
                const maxOffset = Math.max(0, tileCount - 1);
                const newOffset = Math.min(ghostState.scrollOffset + TILES_PER_PAGE, maxOffset);
                applyGhostScroll(dispatch, ghostState, ghostState.carouselIndex, newOffset);
                dispatch(checkAndLoadMoreFeedItems());
                break;
            }
            case InteractiveComponents.WEST: {
                if (ghostState.carouselIndex == null) break;
                const newOffset = Math.max(ghostState.scrollOffset - TILES_PER_PAGE, 0);
                applyGhostScroll(dispatch, ghostState, ghostState.carouselIndex, newOffset);
                break;
            }
            default:
        }
    };
